import type {
  CustomerFieldBindingTransform,
  CustomerFieldMappingTarget,
} from '@/contracts/customer-application-binding'
import { customerFieldTargetOptions } from '@/contracts/customer-application-binding'
import type { CustomerFieldMappingContract } from '@/contracts/customers'

type MiaolinFieldMappingInput = {
  no: number
  questionId?: string
  idSuffix?: string
  title: string
  targetField: CustomerFieldMappingTarget
  required: boolean
  transform?: CustomerFieldBindingTransform
}

const fieldMappings: MiaolinFieldMappingInput[] = [
  { no: 1, title: '新客戶建檔選擇', targetField: 'formType', required: true },
  { no: 2, title: '資料變更選擇', targetField: 'changeType', required: true },
  { no: 3, title: '既有客戶代號', targetField: 'existingCustomerCode', required: true },
  { no: 4, title: '新客戶來源', targetField: 'customerSource', required: true },
  {
    no: 5,
    title: '客戶簡稱',
    targetField: 'customerShortName',
    required: false,
    idSuffix: 'existing-lookup',
  },
  { no: 6, title: '客戶簡稱', targetField: 'customerShortName', required: true },
  { no: 7, title: '統編抬頭（公司全名）', targetField: 'companyFullName', required: false },
  { no: 8, title: '負責人', targetField: 'ownerName', required: true },
  { no: 9, title: '聯絡人', targetField: 'contactName', required: true },
  { no: 10, title: '市話', targetField: 'phone1', required: true, transform: 'phone' },
  { no: 11, title: '手機', targetField: 'phone2', required: false, transform: 'phone' },
  { no: 12, title: '傳真號碼', targetField: 'fax', required: false, transform: 'phone' },
  { no: 13, title: 'E-Mail', targetField: 'email', required: false, transform: 'email' },
  { no: 14, title: '統一編號', targetField: 'taxId', required: false, transform: 'trim' },
  { no: 17, title: '員工人數', targetField: 'employeeCountRange', required: true },
  { no: 18, title: '送貨地址', targetField: 'deliveryAddress', required: true },
  { no: 19, title: '發票地址', targetField: 'invoiceAddress', required: true },
  { no: 20, title: '登記地址', targetField: 'registeredAddress', required: true },
  {
    no: 21,
    title: '帳單收件Mail',
    targetField: 'billingEmail',
    required: false,
    transform: 'email',
  },
  { no: 22, title: '可收貨時間', targetField: 'deliveryTimeNote', required: true },
  { no: 23, title: '營業時段、公休日', targetField: 'businessHours', required: false },
  { no: 24, title: '部門別', targetField: 'department', required: true },
  { no: 25, title: '特販業務', targetField: 'salesRepSpecial', required: true },
  { no: 26, title: '營一業務', targetField: 'salesRepDiv1', required: true },
  { no: 27, title: '營二業務', targetField: 'salesRepDiv2', required: true },
  { no: 28, title: '營三業務', targetField: 'salesRepDiv3', required: true },
  { no: 29, title: '特通業務', targetField: 'salesRepSpecialCh', required: true },
  { no: 30, title: '營四業務', targetField: 'salesRepDiv4', required: true },
  { no: 32, title: '客戶類別', targetField: 'customerCategory', required: false },
  { no: 33, title: '客戶通路別', targetField: 'customerChannel', required: false },
  { no: 34, title: '型態別', targetField: 'customerType', required: false },
  { no: 35, title: '客戶區域別', targetField: 'region', required: true },
  { no: 36, title: '臺北市哪一區？', targetField: 'districtTaipei', required: true },
  { no: 37, title: '新北市哪一區？', targetField: 'districtNewTaipei', required: true },
  { no: 38, title: '臺中市哪一區？', targetField: 'districtTaichung', required: true },
  { no: 39, title: '臺南市哪一區？', targetField: 'districtTainan', required: true },
  { no: 40, title: '高雄市哪一區？', targetField: 'districtKaohsiung', required: true },
  { no: 41, title: '備註', targetField: 'remark', required: false },
  { no: 42, title: '填單人員', targetField: 'submittedBy', required: true },
  { no: 43, title: '食品業者登入字號', targetField: 'foodBusinessRegNo', required: false },
]

export function createMiaolinCustomerFieldMappings(formId: string): CustomerFieldMappingContract[] {
  return fieldMappings.map((mapping) => ({
    ...createMiaolinCustomerFieldMapping(formId, mapping),
  }))
}

function createMiaolinCustomerFieldMapping(
  formId: string,
  mapping: MiaolinFieldMappingInput,
): CustomerFieldMappingContract {
  const targetOption = customerFieldTargetOptions.find(
    (option) => option.value === mapping.targetField,
  )
  return {
    id: `map-miaolin-${String(mapping.no).padStart(2, '0')}${
      mapping.idSuffix ? `-${mapping.idSuffix}` : ''
    }-${mapping.targetField}`,
    formId,
    questionId: mapping.questionId ?? `q-miaolin-${String(mapping.no).padStart(2, '0')}`,
    questionTitle: mapping.title,
    targetField: mapping.targetField,
    targetLabel: targetOption?.shortLabel ?? targetOption?.label,
    dbFieldName: targetOption?.dbFieldName,
    dataType: targetOption?.dataType,
    specQuestionNo: mapping.no,
    transform: mapping.transform ?? 'trim',
    requiredForApproval: mapping.required,
    overwritePolicy: 'prefill_if_empty',
    confidenceHint: 'exact',
  }
}
